import React, { useCallback, useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus } from "@fortawesome/free-solid-svg-icons";
import { toast } from "react-toastify";
import useAuthUser from "react-auth-kit/hooks/useAuthUser";
import Search from "../../common/events/SearchEvents";
import LoadingSpinner from "../../../components/LoadingSpinner";
import EventList from "../../coach/dashboard/EventList";
import AttendeesModal from "../../coach/dashboard/AttendeesModal";
import Modal from "../../../components/Modal";
import AthleteScore from "../../../components/athlete-scores/athlete-score";
import {
  eventErrorAction,
  eventLoadingAction,
  eventsSearchAction,
} from "../../../store/slices/eventSlice";
import {
  SearchEventsByParams,
  getAttendees,
} from "../../../services/EventsService";
import { useAxiosInstance } from "../../../services/Interceptor";
import { useNavigate } from "react-router-dom";

function Combine() {
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredEvents, setFilteredEvents] = useState([]);
  const [attendees, setAttendees] = useState([]);
  const [loadingAttendees, setLoadingAttendees] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [selectedAthlete, setSelectedAthlete] = useState(null);
  const [showResults, setShowResults] = useState(false);
  const [selectedResultAthlete, setSelectedResultAthlete] = useState(null);

  const authUser = useAuthUser();
  const user = authUser?.user;

  const r_events = useSelector((state) => state.events.events);
  const loading = useSelector((state) => state.events.loading);
  const dispatch = useDispatch();
  const apiInstance = useAxiosInstance();
  const navigate = useNavigate();

  const loadEvents = useCallback(async () => {
    dispatch(eventLoadingAction(true));
    try {
      const response = await SearchEventsByParams(apiInstance, "");
      dispatch(eventsSearchAction(response));
    } catch (err) {
      dispatch(eventErrorAction(err.message || "An error occurred"));
    } finally {
      dispatch(eventLoadingAction(false));
    }
  }, [dispatch, apiInstance]);

  useEffect(() => {
    loadEvents();
  }, [loadEvents]);

  useEffect(() => {
    const normalized = searchTerm.trim().toLowerCase();
    if (normalized === "") {
      setFilteredEvents(r_events);
    } else {
      const result = r_events.filter((event) =>
        event.name?.toLowerCase().includes(normalized),
      );
      setFilteredEvents(result);
    }
  }, [searchTerm, r_events]);

  const handleSearch = (query) => {
    setSearchTerm(query);
  };

  const toggleModal = async (event = null) => {
    if (event) {
      setSelectedEvent(event);
      setLoadingAttendees(true);
      try {
        const data = await getAttendees(apiInstance, event.id);
        setAttendees(data);
      } catch (err) {
        toast.error(err.message || "Error loading attendees");
      } finally {
        setLoadingAttendees(false);
      }
    } else {
      setSelectedEvent(null);
      setAttendees([]);
      setShowResults(false);
      setSelectedResultAthlete(null);
    }
  };

  const handleAddEventClick = () => {
    navigate("/admin/calendar?openForm=true");
  };

  return (
    <>
      <div className="flex flex-col p-4 pb-0 rounded-md bg-white lg:h-full">
        <div className="flex justify-between items-center">
          <h1 className="text-xl mb-4 uppercase">COMBINES</h1>
          <FontAwesomeIcon
            icon={faPlus}
            className="svg-inline--fa fa-plus fa-sm bg-[#5057FF] text-white p-2 rounded cursor-pointer -mt-4"
            onClick={handleAddEventClick}
          />
        </div>

        <div className="mb-0 w-full relative">
          <Search onSearch={handleSearch} placeholder="Search Events" />
        </div>

        <div className="pr-4 -mr-4 overflow-auto">
          {loading ? (
            <LoadingSpinner />
          ) : (
            <div className="pr-4 -mr-4 overflow-auto">
              {filteredEvents.length > 0 ? (
                filteredEvents.map((event) => (
                  <EventList
                    key={event.id || event._id}
                    event={event}
                    showAttendeesButton={user?.role === "admin"}
                    onViewAttendees={() => toggleModal(event)}
                  />
                ))
              ) : (
                <div className="w-full p-2 mt-5 flex flex-col items-center">
                  <img
                    alt="No events that match your search"
                    src="/nodata.gif"
                    className="object-cover h-20 w-20 rounded-full border-gray-300 border"
                  />
                  <span className="mt-4 text-center text-md text-slate-600">
                    There are no combines that match your search
                  </span>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <div className="p-4 bg-white rounded-lg shadow-lg">
        <div className="flex justify-between items-center">
          <h1 className="text-xl mb-4 uppercase">COMBINES</h1>
        </div>
      </div>

      {selectedEvent && (
        <AttendeesModal
          event={selectedEvent}
          attendees={attendees}
          loading={loadingAttendees}
          onClose={() => toggleModal()}
          setSelectedAthlete={setSelectedAthlete}
          onViewResults={(athlete) => {
            setSelectedResultAthlete(athlete);
            setShowResults(true);
          }}
          userRole={user?.role}
        />
      )}

      {selectedAthlete && (
        <Modal
          isOpen={true}
          onClose={() => setSelectedAthlete(null)}
          backdrop="static"
          keyboard={false}
        >
          <AthleteScore
            userId={selectedAthlete.userId}
            showOffers={true}
            showTrackittScore={true}
            showPositionScore={true}
            searchEvent={false}
            showProfile={true}
            showRank={true}
            showLikes={true}
          />
        </Modal>
      )}

      {showResults && selectedResultAthlete && (
        <Modal
          isOpen={true}
          onClose={() => {
            setShowResults(false);
            setSelectedResultAthlete(null);
          }}
          backdrop="static"
          keyboard={false}
        >
          <h2 className="text-xl p-4 mt-1 font-semibold">
            Results for {selectedResultAthlete.name} - {selectedEvent.name}
          </h2>
          <AthleteScore
            key={`admin-${selectedResultAthlete?.userId}`}
            userId={selectedResultAthlete.userId}
            showOffers={false}
            showTrackittScore={true}
            showPositionScore={true}
            searchEvent={false}
            showProfile={false}
            showRank={true}
            showLikes={false}
            eventId={selectedEvent.id}
            sharedProfile={true}
          />
        </Modal>
      )}
    </>
  );
}

export default Combine;
